import _ from 'lodash';
import moment from 'moment-timezone';
import { Images } from '../../../../../../../shared/app-constants';
import { Schedule } from '../../../../../../settings/types/schedule';
import hasPermission from '../../../../../../../shared/utils/access-control/has-permission';
import {
  Sequence,
  SequenceStep,
  SequenceStepVariant,
} from '../../../../../types';
import { Permissions } from '../../../../../../../shared/utils/access-control/enums/permissions';
import { SequenceStepConfig, SequenceStepType } from '../../../../../enums';
import { getUserTimeZone } from '../../../../../../../shared/utils/user-details';
import { Subsequence } from '../../../../../types/subsequence';

// Get Tentative Date Tooltip Text
export const getTentativeDateTooltipText = (t): string =>
  `${t('messages.what_is_tentative_date_1')} ${t(
    'messages.what_is_tentative_date_2',
  )}`;

// Get Sequence Schedule
export const getSequenceSchedule = (
  schedules: Schedule[],
  sequenceScheduleId: number,
) => {
  const sequenceSchedule = schedules.filter(
    (schedule) => schedule.id === sequenceScheduleId,
  )[0];

  // Filter Allowed Days and Time Slot From Sequence Schedule
  const daysAndSlots = sequenceSchedule
    ? JSON.parse(sequenceSchedule.timeSlots).filter(
        (schedule) => schedule.slots.length > 0,
      )
    : [];

  // Sort Slots Based On Day In Asc Order
  return {
    sequenceTimezone: sequenceSchedule?.timezone || '',
    slots: _.sortBy(daysAndSlots, ['slots', 'day']),
  };
};

// Get Step Message Icon
export const getStepMessageIcon = (subject: string): string =>
  subject === '' ? Images.ArrowForward : Images.GreySend;

// Get Step Sending As Message
export const getStepSendingAsMessage = (
  stepType: SequenceStepType,
  stepNumber: number,
  subject: string,
): string => {
  if (
    stepType === SequenceStepType.Email &&
    stepNumber === 1 &&
    subject !== ''
  ) {
    return 'Sending as a new email on';
  }
  if (stepType === SequenceStepType.Email && subject === '') {
    return 'Sending as the same thread on';
  }
  if (stepType === SequenceStepType.LinkedInConnectionRequest) {
    return 'Scheduling Linkedin Connection task on';
  }
  if (stepType === SequenceStepType.LinkedInMessage) {
    return 'Scheduling Linkedin Message task on';
  }
  if (stepType === SequenceStepType.LinkedInInMail) {
    return 'Scheduling Linkedin InMail task on';
  }
  if (stepType === SequenceStepType.LinkedInViewProfile) {
    return 'Scheduling Linkedin View Profile task on';
  }
  if (stepType === SequenceStepType.LinkedInPostInteraction) {
    return 'Scheduling Linkedin Post Interaction task on';
  }
  if (stepType === SequenceStepType.Call) {
    return 'Scheduling Calling task on';
  }
  if (stepType === SequenceStepType.WhatsAppMessage) {
    return 'Scheduling Whatsapp Message task will be due on';
  }
  if (stepType === SequenceStepType.WhatsAppVoiceMessage) {
    return 'Scheduling Whatsapp Voice Message task will be due on';
  }
  if (stepType === SequenceStepType.WhatsAppVoiceCall) {
    return 'Scheduling Whatsapp Voice Call task will be due on';
  }
  if (stepType === SequenceStepType.Custom) {
    return 'Task will be created on';
  }
  if (
    stepType === SequenceStepType.AimfoxCampaignPush ||
    stepType === SequenceStepType.HeyReachCampaignPush
  ) {
    return 'Sending as a new campaign on';
  }

  return 'Sending as a new thread on';
};

// Get Tentative Day
export const getTentativeDate = (scheduledDate: string): string => {
  if (scheduledDate) {
    const userTimeZone = getUserTimeZone();
    if (userTimeZone) {
      return moment(scheduledDate).tz(userTimeZone).format('ddd, Do MMM YYYY');
    }
    return moment(scheduledDate).format('ddd, Do MMM YYYY');
  }
  return '';
};

// Get Step Info Message
export const getStepInfoMessage = (
  stepNumber: number,
  prevStepTentativeDate: string,
): string => {
  if (stepNumber === 1) {
    return 'if activated today';
  }
  return `if step ${stepNumber - 1} is sent on ${prevStepTentativeDate}`;
};

export const getSequenceType = (isSampleSequence) => {
  if (isSampleSequence && isSampleSequence === '1') {
    return 'Sample Sequence';
  }
  return 'New Sequence';
};

const getAbsoluteDaysArr = (sequence) => {
  let ab = 1;
  // eslint-disable-next-line no-return-assign
  return sequence?.steps?.map((step) => ({
    ...step,
    absoluteDay: (ab += step.relativeDays),
  }));
};

// Handle Add or Edit Variant From Query Params
export const handleAddOrEditVariantOnStepOne = ({
  history,
  sequence,
  showAddVariantModal,
  showUpdateVariantDataModal,
  setActiveSidebarTab,
}: {
  history: any;
  sequence: Sequence | Subsequence;
  showAddVariantModal: (step: SequenceStep) => void;
  showUpdateVariantDataModal: (
    step: SequenceStep,
    variant: SequenceStepVariant,
    variantNumber: number,
  ) => void;
  setActiveSidebarTab?: (activeSidebarTab: string) => void;
}) => {
  const queryString = new URLSearchParams(history.location.search || '');
  const addNewVariant = queryString.get('addNewVariant');
  const editVariantId = queryString.get('editVariantId');
  const activeSidebarTab = queryString.get('activeSidebarTab');

  const tempSteps = getAbsoluteDaysArr(sequence);
  const tempSequence = { ...sequence, steps: tempSteps };
  const firstEmailStep = tempSequence?.steps?.filter(
    (s) => s.type === SequenceStepType.Email,
  );

  if (hasPermission(Permissions.SEQUENCE_STEP_WRITE) && !sequence) {
    return;
  }

  if (addNewVariant) {
    if (firstEmailStep[0].variants.length < SequenceStepConfig.MaxVariants) {
      showAddVariantModal(firstEmailStep[0]);
    }

    queryString.delete('addNewVariant');

    history.push({
      search: queryString.toString(),
    });

    return;
  }

  if (editVariantId && editVariantId !== '') {
    let variantNumber = 0;
    let variant = null;
    let step = null;

    firstEmailStep.forEach((stepItem) => {
      stepItem.variants.forEach((item, index) => {
        if (item.id === Number(editVariantId)) {
          variantNumber = index + 1;
          variant = item;
          step = stepItem;
        }
      });
    });

    if (variant) {
      showUpdateVariantDataModal(step, variant, variantNumber);
    }

    queryString.delete('editVariantId');

    history.push({
      search: queryString.toString(),
    });
  }

  if (activeSidebarTab && activeSidebarTab !== '') {
    if (setActiveSidebarTab) {
      setActiveSidebarTab(activeSidebarTab);
    }

    queryString.delete('activeSidebarTab');

    history.push({
      search: queryString.toString(),
    });
  }
};

export const getFocusOnValue = (history): 'subject' | 'body' => {
  const queryString = new URLSearchParams(history.location.search || '');
  const focusOn = queryString.get('focusOn');

  if (focusOn && focusOn === 'body') {
    return 'body';
  }

  return 'subject';
};

export const isAddVariantBtnVisible = (stepType: SequenceStepType) =>
  stepType === SequenceStepType.Email ||
  stepType === SequenceStepType.LinkedInConnectionRequest ||
  stepType === SequenceStepType.LinkedInMessage ||
  stepType === SequenceStepType.LinkedInInMail ||
  stepType === SequenceStepType.WhatsAppMessage ||
  stepType === SequenceStepType.WhatsAppVoiceMessage ||
  stepType === SequenceStepType.WhatsAppVoiceCall;

export const hasAnyEmailStep = (steps: SequenceStep[]) =>
  steps?.some((s) => s?.type === SequenceStepType.Email);

export const getFirstEmailStepId = (steps: SequenceStep[]) => {
  const firstEmailStep = steps?.find((s) => s?.type === SequenceStepType.Email);
  return firstEmailStep ? firstEmailStep?.id : null;
};
